// DossierDetail — Fiche dossier redesignée
// Header fixe + Pipeline 9 étapes + 4 onglets contextuels
import { useState, useEffect, useRef, useCallback } from "react";
import { isInstallateurProfile, hasDossierAmo, hasVisitesTerrain, hasDevisInstallateur, hasDossierSite, hasIntegrationPennylane } from "../../utils/tenantProfile";
import { DossierArtisanView } from "./artisan/DossierArtisanView";
import {
  ArrowLeft, RefreshCw, Loader2, Clock, Shield, MapPin, Banknote,
  Briefcase, Calculator, Building2, Thermometer, FileText, AlertTriangle,
  FolderOpen, Lock, CheckCircle2, XCircle, AlertCircle, HelpCircle,
  ChevronDown, ArrowRight, Info, Trash2, Copy, Check, Send,
  HardHat, MessageSquare, Files, Users, CalendarDays, Wrench, FileSignature, CreditCard,
  HandCoins, ExternalLink, Archive,
  ClipboardList,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";

// Internal components
import { PipelineStepper, isAbandonne } from "./dossier/PipelineStepper";
import usePipeline from "../../hooks/usePipeline";
import { prochaineEtapeFiche, phraseOuEnEst, etapeDuDossier } from "./dossier/prochaineEtapeFiche";
import DatesPipelineBlock from "./dossier/DatesPipelineBlock";
import { GovernanceIndicator } from "./dossier/GovernanceIndicator";
import PorteDecision from "../../components/metier/portes/PorteDecision";
import { InfosTab } from "./dossier/InfosTab";
import { ACompleterBlock } from "./dossier/ACompleterBlock";
import { useChecklistAnah } from "./dossier/checklistAnah";
import { DALMobileBlock } from "./dossier/DALMobileBlock";
import { CommercialSection } from "./dossier/CommercialSection";
import { OperationSection } from "./dossier/OperationSection";
import { EnvoyerVisiteBlock } from "./dossier/EnvoyerVisiteBlock";
import { ConformiteEditableTab } from "./dossier/ConformiteEditableTab";
import { PlanFinancementBlock } from "./dossier/PlanFinancementBlock";
import { FinanceSection } from "./dossier/FinanceSection";
import { FinancementTab } from "./dossier/FinancementTab";
import { AidesComplementairesTab } from "./dossier/AidesComplementairesTab";
import { DocumentsForm } from "./dossier/DocumentsForm";
import { HistoriqueModal } from "./dossier/HistoriqueModal";
import SendMessageModal from "./dossier/SendMessageModal";
import { CommentairesDossier } from "./dossier/CommentairesDossier";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { TutoModal } from "../../components/TutoModal";
import { DossierInstallateurView } from "./dossier/DossierInstallateurView";
import { DossierVisiteurView } from "./dossier/DossierVisiteurView";
import { API, getAuthHeaders, STATUTS_CONFIG, useStatutsConfig } from "./dossier/helpers";
import { ChangerStatutDialog } from "./dossier/ChangerStatutDialog";
import { MettreDeCoteDialog, mettreDeCote } from "./dossier/MettreDeCoteDialog";
import { OutilsAvancesMenu } from "./dossier/OutilsAvancesMenu";
import { completudeParOnglet } from "./dossier/completude";
import { SynchroPennylaneDialog } from "./dossier/SynchroPennylaneDialog";
import { groupesStatuts, libelleStatut } from "./dossier/statutsDossier";
import { openAuthedPdf } from "./dossier/helpers";
// 🔑 LA MÊME date française que partout — ⛔ pas un second format.
import { formatDateFR } from "../../utils/dateFormat";

// External components
import PhotosChantier from "./PhotosChantier";
import { PhotosForm } from "./dossier/PhotosForm";
import CommandesManager from "../operation/CommandesManager";
import PlanningChantier from "../operation/PlanningChantier";
import AnahLinkTab from "../../components/metier/AnahLinkTab";
import DALForm from "../../components/tef/DALForm";
import DALConsultation from "../../components/tef/DALConsultation";
import { DesktopDALWizard } from "./dossier/DesktopDALWizard";
import RedigerAvecMatthieuPanel from "./dossier/RedigerAvecMatthieuPanel";
import { SplitViewLayout } from "./dossier/SplitViewLayout";
import DossierOmvView from "../../components/omv/DossierOmvView";
import { formatClientFullName } from "../../utils/clientFormat";
import { enteteTenant } from "../../utils/enteteTenant";

// ─── Onglets config ───
// Refonte P1.1 — ordre process TEF : Infos · Demande MPA · DAL · Finance · Conformité.
// La clé interne "operation" est CONSERVÉE (routage HUB_TO_TAB / getInitialTab /
// headerAction / complétude) ; seul le LABEL devient "Demande MPA".
const TABS = [
  { key: "infos", label: "Infos", icon: Info, color: "var(--oh-hub-commercial)" },
  { key: "operation", label: "Demande MPA", labelCore: "Opération", icon: Briefcase, color: "var(--oh-hub-operation)" },
  { key: "visites", label: "Visites", icon: ClipboardList, color: "var(--oh-hub-operation)" },
  { key: "dal", label: "DAL", icon: FileText, color: "var(--oh-stat-orange-text, #fb923c)" },
  { key: "finance", label: "Finance", icon: Banknote, color: "var(--oh-hub-finance, #10b981)" },
  { key: "conformite", label: "Conformité", icon: Shield, color: "var(--oh-hub-conformité, var(--oh-stat-amber-text))" },
];

// ─── Split View (prototype design JJ) — menus master-detail intra-onglet ───
// Les `id` correspondent aux `data-subnav-id` DÉJÀ présents dans les composants
// de section existants (CommercialSection / OperationSection). On NE recrée rien :
// SplitViewLayout affiche à droite le bloc dont l'id est sélectionné à gauche.
// PN-11 : le bloc « Chantier » ET son entrée de menu suivent UNE condition. Avant, le bloc ne s'affichait que pour 7 anciens statuts (chantier_*, travaux_*) que
// les dossiers du pipeline actuel n'ont plus : le Chantier restait invisible de l'octroi au solde. Désormais : la phase des travaux = de l'octroi au solde.
const STATUTS_CHANTIER = ["chantier_a_planifier", "chantier_planifie", "chantier_en_cours", "chantier_termine", "travaux_planifies", "travaux_en_cours", "travaux_termines"];
const ETAPES_CHANTIER = ["octroi", "demande_avance", "avance_payee", "demande_solde", "solde_paye"];
export const afficherChantier = (dossier, etapePipeline) => STATUTS_CHANTIER.includes(dossier?.statut) || ETAPES_CHANTIER.includes(etapePipeline);

const SPLIT_SECTIONS_OPERATION = [
  { id: "contrats",      label: "Contrats & Engagement", icon: FileSignature, color: "var(--oh-hub-commercial)" },
  { id: "visiteur",      label: "Visiteur",              icon: Users,         color: "var(--oh-stat-blue-text)" },
  { id: "visite",        label: "Planning RDV",          icon: CalendarDays,  color: "var(--oh-hub-operation)" },
  { id: "installateur",  label: "Installateur",          icon: Wrench,        color: "var(--oh-stat-blue-text)" },
  { id: "prestation-amo",label: "Prestation",            icon: Briefcase,     color: "var(--oh-hub-commercial)" },
  { id: "chantier",      label: "Chantier",              icon: Building2,     color: "var(--oh-hub-operation)" },
  { id: "financement",   label: "Financement",           icon: Banknote,      color: "var(--oh-hub-finance, #10b981)" },
];
const SPLIT_SECTIONS_DAL = [
  { id: "dal-mobile",    label: "DAL Mobile",            icon: FileText,      color: "var(--oh-stat-orange-text, #fb923c)" },
  { id: "dal-wizard",    label: "Assistant DAL",         icon: FileText,      color: "var(--oh-stat-orange-text, #fb923c)" },
  { id: "dal-matthieu",  label: "Rédiger avec Matthieu", icon: MessageSquare, color: "var(--oh-stat-orange-text, #fb923c)" },
  { id: "dal-classique", label: "DAL",                   icon: Files,         color: "var(--oh-text-muted)" },
];
// Onglet Infos — 4 blocs EXISTANTS de InfosTab.jsx (ancrés data-subnav-id).
const SPLIT_SECTIONS_INFOS = [
  { id: "beneficiaire",  label: "Bénéficiaire",          icon: Users,         color: "var(--oh-hub-commercial)" },
  { id: "logement",      label: "Logement",              icon: MapPin,        color: "var(--oh-hub-operation)" },
  { id: "mpa",           label: "MaPrimeAdapt'",         icon: Calculator,    color: "var(--oh-stat-amber-text)" },
  { id: "compte-anah",   label: "Compte ANAH",           icon: Lock,          color: "var(--oh-stat-blue-text)" },
];
// Onglet Finance — 3 blocs EXISTANTS de FinanceSection.jsx (déjà ancrés).
const SPLIT_SECTIONS_FINANCE = [
  { id: "facture-amo",       label: "Facture AMO",       icon: Banknote,      color: "var(--oh-hub-finance, #10b981)" },
  { id: "paiement",          label: "Règlements AMO",    icon: CreditCard,    color: "var(--oh-hub-finance, #10b981)" },
  { id: "paiement-visiteur", label: "Visite payée",      icon: Users,         color: "var(--oh-stat-amber-text)" },
];
// LOT B — blocs « AMO » de l'onglet Finance : masqués (entrée du menu ET bloc) si le tenant
// ne déclare pas la capacité « dossier AMO » (hasDossierAmo). Exporté pour le test.
export const SECTIONS_FINANCE_AMO = new Set(["facture-amo", "paiement"]);
// Onglet Conformité — 2 blocs cohérents : trigger Liaison ANAH + carte Vérification
// (ConformiteEditableTab garde son header/save/progress + accordéons internes).
const SPLIT_SECTIONS_CONFORMITE = [
  { id: "liaison-anah",        label: "Liaison ANAH",          icon: Shield,        color: "var(--oh-stat-green-text, #10b981)" },
  { id: "plan-financement",    label: "Plan de financement",   icon: Calculator,    color: "var(--oh-stat-purple-text)" },
  { id: "aides-complementaires", label: "Aides complémentaires", icon: HandCoins,   color: "var(--oh-hub-aides, var(--oh-stat-cyan-text))" },
  { id: "verification",        label: "Vérification",          icon: CheckCircle2,  color: "var(--oh-stat-amber-text)" },
];

// Mapping hub d'origine → onglet par défaut
const HUB_TO_TAB = {
  commercial: "infos",
  operation: "operation",
  conformite: "conformite",
  conformité: "conformite",
  aides: "finance",
  finance: "finance",
};

// ─── Main Component ───
export default function DossierDetail({ dossierId, tenantId, onBack, onNavigate, hubContext }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  // PN-6 : le pipeline du tenant (même source que le stepper) — sans lui l'étape ne se résout pas.
  const { pipeline } = usePipeline();
  // PN-7 : la liste de statuts du tenant — la MÊME pour le menu et pour le badge.
  const { config: statutsCfg } = useStatutsConfig();
  const [showChangerStatut, setShowChangerStatut] = useState(false);
  const [showMettreDeCote, setShowMettreDeCote] = useState(false);   // PN-8
  const [showSynchroPennylane, setShowSynchroPennylane] = useState(false);   // PN-11
  const [dossier, setDossier] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [historique, setHistorique] = useState([]);
  const [analyzingAgent, setAnalyzingAgent] = useState(false);
  const [agentProgress, setAgentProgress] = useState(null);
  const [blockedTransitions, setBlockedTransitions] = useState([]);
  const [showHistorique, setShowHistorique] = useState(false);
  const [showTuto, setShowTuto] = useState(false);
  const [showSendMessage, setShowSendMessage] = useState(false);
  // Refonte P1.1 — panneaux Documents & Commentaires exposés depuis le header (actions nommées)
  const [showDocuments, setShowDocuments] = useState(false);
  // T-364 (F-4) : arrivée depuis le Bureau AMO (« Voir les échanges ») ou depuis l'Historique : Commentaires filtrés sur les échanges mail.
  const [filtreCommentaires, setFiltreCommentaires] = useState(() => (typeof window !== "undefined" && window.__dossierVoirEchanges ? "echanges" : "tous"));
  const [showCommentaires, setShowCommentaires] = useState(() => typeof window !== "undefined" && window.__dossierVoirEchanges === true);
  useEffect(() => { if (typeof window !== "undefined") window.__dossierVoirEchanges = false; }, []);
  // Conformité — Liaison ANAH (données importées) + Plan financement CERFA en MODALE.
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [syncingZoho, setSyncingZoho] = useState(false);
  const initialLoadDone = useRef(false);
  // Navigation programmatique du split DAL (Consultation « Modifier » → « Assistant DAL »).
  const dalNavRef = useRef(null);
  const conformiteNavRef = useRef(null);   // T-367c : bascule la section « Vérification » de l'onglet Conformité (où vivent « Vérification Conformité » et « Préparer le dépôt »)

  const handleResyncZoho = useCallback(async () => {
    if (!dossierId || syncingZoho) return;
    setSyncingZoho(true);
    try {
      const headers = getAuthHeaders(tenantId);
      const res = await fetch(
        `${API}/api/metier/tef/zoho/resync-dossier/${dossierId}?push_to_zoho=false`,
        { method: "POST", headers, credentials: "include" }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      const fz = data.from_zoho_documents || {};
      const fp = data.from_zoho_photos || {};
      const imp = (fz.imported || 0) + (fp.imported || 0);
      const lnk = (fz.linked || 0) + (fp.linked || 0);
      const skp = (fz.skipped || 0) + (fp.skipped || 0);
      toast.success(`Sync Zoho OK — ${imp} importé(s), ${lnk} lié(s), ${skp} déjà à jour`);
      loadDossier();
    } catch (e) {
      toast.error(`Sync Zoho échoué : ${e.message}`);
    } finally {
      setSyncingZoho(false);
    }
  }, [dossierId, tenantId, syncingZoho]);

  // Active tab — from URL ?tab= or hubContext or default
  const getInitialTab = () => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get("tab");
    if (tabParam && TABS.find(t => t.key === tabParam)) return tabParam;
    // T-367 : arrivée depuis la file de travail AMO — l'onglet est lu une fois, puis remis à zéro (il ne reste pas collé aux ouvertures suivantes).
    const ongletFile = typeof window !== "undefined" ? window.__dossierOnglet : null;
    if (typeof window !== "undefined") window.__dossierOnglet = null;
    if (ongletFile && TABS.find(t => t.key === ongletFile)) return ongletFile;
    if (hubContext && HUB_TO_TAB[hubContext]) return HUB_TO_TAB[hubContext];
    return "infos";
  };
  const [activeTab, setActiveTab] = useState(getInitialTab);
  // T-367c : arrivée de la file de travail AMO sur le BLOC à traiter (panneau « Préparer le dépôt », « Vérification Conformité », « Rédiger avec
  // Matthieu »), pas seulement sur l'onglet. Lu une fois (puis remis à zéro). Les panneaux se chargent après la fiche : on les attend, 4 s au plus.
  const blocDemande = useRef(typeof window !== "undefined" ? window.__dossierBloc || null : null);
  useEffect(() => { if (typeof window !== "undefined") window.__dossierBloc = null; }, []);
  useEffect(() => {
    const bloc = blocDemande.current;
    if (!bloc || !dossier) return undefined;
    let essais = 0;
    const t = setInterval(() => {
      essais += 1;
      let trouve = false;
      if (bloc === "dal-matthieu") {
        if (activeTab === "dal" && dalNavRef.current) { dalNavRef.current("dal-matthieu"); trouve = true; }
      } else {
        if (activeTab === "conformite" && conformiteNavRef.current && essais === 1) conformiteNavRef.current("verification");   // le panneau n'est affiché que dans cette section
        const el = document.querySelector(`[data-testid="${bloc}"]`);
        let visible = Boolean(el);
        for (let n = el; visible && n && n !== document.body; n = n.parentElement) if (n.style && n.style.display === "none") visible = false;   // section masquée du split
        if (el && visible) {
          el.scrollIntoView?.({ block: "start", behavior: "smooth" });
          el.style.outline = "2px solid var(--oh-accent)";           // repère visible, retiré au bout de quelques secondes
          setTimeout(() => { el.style.outline = ""; }, 4000);
          trouve = true;
        }
      }
      if (trouve || essais >= 20) { clearInterval(t); blocDemande.current = null; }
    }, 200);
    return () => clearInterval(t);
  }, [activeTab, dossier]);
  const [refCopied, setRefCopied] = useState(false);
  const copyRef = useCallback(async () => {
    const ref = dossier?.reference || dossier?.référence;
    if (!ref) return;
    try {
      await navigator.clipboard.writeText(ref);
      setRefCopied(true);
      toast.success(`Référence ${ref} copiée`);
      setTimeout(() => setRefCopied(false), 1500);
    } catch {
      toast.error("Impossible de copier");
    }
  }, [dossier]);

  // Role detection
  const userRole = user?.role || user?.rôle || "user";
  const isTEFRole = ["admin", "super_admin", "manager"].includes(userRole) || user?.is_super_admin;
  const estSuperAdmin = userRole === "super_admin" || user?.is_super_admin === true;   // PN-8 : outils avancés
  const isInstallateurRole = userRole === "external" || userRole === "installateur";
  const isVisiteurRole = userRole === "visiteur";
  const effectiveRole = isTEFRole ? "tef" : isInstallateurRole ? "installateur" : isVisiteurRole ? "visiteur" : "tef";

  // 🔴🔴 N°752 (ordre imposé par JJ) — LES DEUX DERNIÈRES DÉRIVATIONS PAR SLUG
  // DE CE FICHIER SONT SUPPRIMÉES. Elles valaient
  //     dossier.activite === "tef" || tenantId === "tef"
  // c'est-à-dire un comportement métier décidé par un SLUG et par une valeur
  // d'activité — la règle en creux que les N°705/N°728/N°747 retirent partout
  // ailleurs. Elles pilotaient 12 gates (onglet DAL, Conformité, checklist ANAH)
  // et 4 props d'enfants : c'est pour ça qu'elles avaient survécu.
  //
  // ⚠️ CE QUI REND LA SUBSTITUTION FIDÈLE, ET CE QUI LA CONDITIONNE — MESURÉ le
  // 2026-09-27 sur `omnihub_preprod` : chaque tenant est MONO-ACTIVITÉ
  //     tef 273 « tef » · demo 9 « tef » · mario_bat 59 « renovation »
  //     omv_france 13 « climat » · climatpac 2 « climat »
  // ⇒ `dossier.activite === "tef"` et « le tenant déclare l'AMO » désignent
  // AUJOURD'HUI le même ensemble. 🔴 DÉPENDANCE : `demo` ⛔ ne déclarait PAS
  // `config.dossier_amo` (mesuré) — la migration de CC AUDIT (N°752) pose
  // `regime_aides: ["anah"]` ET `dossier_amo` sur `demo`, et elle doit être
  // JOUÉE AVANT que ce bundle ne soit servi, sinon les 9 dossiers de `demo`
  // perdent l'onglet DAL et la Conformité.
  // ⚠️ SI un tenant devenait MIXTE (dossiers d'activités différentes), la
  // capacité — qui est par TENANT — ⛔ ne suffirait plus : il faudrait alors une
  // capacité PAR DOSSIER, ⛔ pas un retour au slug.

  // 🔴 LA COMPOSITION MÉTIER VIENT DE LA CAPACITÉ DÉCLARÉE, ⛔ PLUS DU RÔLE.
  //
  // ⚠️ CE QUI SE PASSAIT, mesuré le 18/09 : `effectiveRole` valait "tef" dès que
  // l'utilisateur était admin (`isTEFRole` = un nom qui MENT, il teste
  // ["admin","super_admin","manager"]), ET la ternaire retombait sur "tef" en
  // dernier repli. Un dossier `mario_bat` / `activite=renovation` / profil CORE
  // recevait donc MaPrimeAdapt', Compte ANAH, DAL, Finance AMO et Conformité
  // ANAH — parce que celui qui le REGARDAIT était administrateur.
  // 🔑 LE RÔLE GOUVERNE LES DROITS ; LA CAPACITÉ GOUVERNE LA COMPOSITION.
  // ⛔ Absence de déclaration ⛔ n'est PAS permission d'hériter de TEF.
  const amo = hasDossierAmo();
  // T-242d — UNE lecture de la checklist ANAH (moteur `check_anah_checklist`) pour « À compléter » et l'onglet Conformité.
  const { checklist: checklistAnah, recharger: rechargerChecklistAnah } = useChecklistAnah(dossier?.reference || dossier?.ref, tenantId, amo);
  // 🔴🔴 N°705-4 et N°705-7 — L'ENTRÉE DE SOUS-MENU, ⛔ pas seulement le
  // contenu. ⚠️ MESURÉ : le CONTENU du bloc « Installateur » est gaté depuis
  // le N°690-2, mais SON ENTRÉE de menu ⛔ n'était filtrée POUR PERSONNE — le
  // menu annonçait donc une section VIDE chez Mario.
  // ⚠️ Le bloc « Visite » de cet onglet est le PLANNING (date de RDV, Google
  // Calendar, statut_rdv) d'un intervenant qu'on ENVOIE : tous ses champs sont
  // vides chez Mario, qui fait SES visites lui-même par l'application terrain.
  // ⛔ CE ⛔ N'EST PAS le panneau « Dossier ▸ Visites » (onglet à part, qui lui
  // FONCTIONNE) : deux objets distincts, ⛔ un seul est touché ici.
  // 🔑 CAPABILITY : `devis_installateur_requis` — DÉCLARÉE par tef,
  // omv_france ET climatpac (mesuré), ⛔ pas par mario_bat. Les trois voient
  // donc EXACTEMENT ce qu'ils voyaient.
  // ⏸️ ⚠️ SI JJ veut une capability PROPRE au planning (« planning_rdv »), elle
  // se déclare en une ligne de configuration — ⛔ je ⛔ n'en INVENTE PAS une
  // seul, et j'ai pris celle qui existe et qui sépare juste.
  const installateur = hasDevisInstallateur();
  // 🔴 L'ONGLET « VISITES » SUIT LA CAPACITÉ TERRAIN — même méthode que l'AMO.
  // ⚠️ La relation dossier↔visite EXISTE en base depuis toujours
  // (`saisie.py:119`) ; ⛔ AUCUN écran ⛔ ne permettait de la parcourir. On
  // EXPOSE, ⛔ on ⛔ ne crée RIEN.
  const terrain = hasVisitesTerrain();
  const TABS_DECLARES = TABS.filter(
    t => (amo || !["dal", "finance", "conformite"].includes(t.key))
         && (t.key !== "visites" || terrain));
  // ⚠️ Le filtre par RÔLE reste — mais il ne porte plus que sur l'ÉTENDUE de ce
  // qu'un profil restreint (installateur, visiteur) a le droit de voir, ⛔ plus
  // sur QUELS MÉTIERS existent.
  const visibleTabs = (effectiveRole === "tef"
    ? TABS_DECLARES
    : TABS_DECLARES.filter(t => ["infos", "operation", "conformite"].includes(t.key))
  ).filter(t => t.key !== "dal" || amo);

  useEffect(() => { if (dossierId) loadDossier(); }, [dossierId]);

  const loadDossier = async () => {
    if (!initialLoadDone.current) setLoading(true);
    try {
      const headers = getAuthHeaders(tenantId);
      const [res, docsRes, histRes] = await Promise.all([
        fetch(`${API}/api/metier/dossiers/${dossierId}`, { headers, credentials: "include" }),
        fetch(`${API}/api/metier/dossiers/${dossierId}/documents`, { headers, credentials: "include" }),
        fetch(`${API}/api/metier/dossiers/${dossierId}/historique`, { headers, credentials: "include" }),
      ]);
      let dossierData = null;
      if (res.ok) { dossierData = await res.json(); setDossier(dossierData); }
      if (docsRes.ok) setDocuments(await docsRes.json());
      if (histRes.ok) setHistorique(await histRes.json());
      if (dossierData?.client_id && !dossierData?.client_nom) {
        try {
          const clientRes = await fetch(`${API}/api/metier/clients/${dossierData.client_id}`, { headers, credentials: "include" });
          if (clientRes.ok) {
            const cd = await clientRes.json();
            setDossier(prev => prev ? { ...prev, client_nom: formatClientFullName(cd) } : prev);
          }
        } catch (e) { console.warn("Client load error:", e); }
      }
    } catch (err) { console.error("Erreur chargement dossier:", err); }
    finally { setLoading(false); initialLoadDone.current = true; }
  };

  const handleLaunchAgent = async () => {
    if (!dossier?.client_id) { toast.error("Pas de client_id associé au dossier"); return; }
    setAnalyzingAgent(true);
    setAgentProgress("Lancement de l'analyse...");
    try {
      const headers = getAuthHeaders(tenantId, "application/json");
      const res = await fetch(`${API}/api/conformite/agent/analyze/${dossier.client_id}`, { method: "POST", headers, credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        const taskId = data.task_id;
        toast.success("Analyse lancée...");
        const pollInterval = setInterval(async () => {
          try {
            const statusRes = await fetch(`${API}/api/conformite/agent/task-status/${taskId}`, { headers, credentials: "include" });
            if (statusRes.ok) {
              const status = await statusRes.json();
              if (status.status === "completed") {
                clearInterval(pollInterval);
                setAnalyzingAgent(false); setAgentProgress(null);
                toast.success(`Analyse terminée : ${[...(status.fields_updated_client || []), ...(status.fields_updated_dossier || [])].length} champs mis à jour`);
                loadDossier();
              } else if (status.status === "error") {
                clearInterval(pollInterval);
                setAnalyzingAgent(false); setAgentProgress(null);
                toast.error(status.error || "Erreur");
              } else { setAgentProgress(`Analyse en cours... (${status.phase || "extraction"})`); }
            }
          } catch {}
        }, 3000);
        setTimeout(() => { clearInterval(pollInterval); setAnalyzingAgent(false); setAgentProgress(null); loadDossier(); }, 300000);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Erreur lancement agent");
        setAnalyzingAgent(false); setAgentProgress(null);
      }
    } catch { toast.error("Erreur réseau"); setAnalyzingAgent(false); setAgentProgress(null); }
  };

  // Delete dossier handler (admin only)
  const handleDeleteDossier = async () => {
    const ref = dossier?.référence || dossier?.reference || dossierId;
    const confirmed = window.prompt(`Pour confirmer la suppression du dossier ${ref}, tapez "SUPPRIMER"`);
    if (confirmed !== "SUPPRIMER") return;
    try {
      const res = await fetch(`${API}/api/metier/dossiers/${dossierId}`, {
        method: "DELETE",
        headers: { ...(localStorage.getItem("session_token") && { Authorization: `Bearer ${localStorage.getItem("session_token")}` }), ...enteteTenant(tenantId) },
        credentials: "include",
      });
      if (res.ok) { toast.success("Dossier supprimé"); onBack?.(); }
      else { const e = await res.json().catch(() => ({})); toast.error(e.detail || "Erreur suppression"); }
    } catch { toast.error("Erreur réseau"); }
  };

  // PN-8 — « Mettre de côté » : l'archivage existant, avec une RAISON obligatoire (rien n'est effacé).
  const handleMettreDeCote = async (raison) => {
    setShowMettreDeCote(false);
    const { ok } = await mettreDeCote([dossier?.id || dossierId], raison, { api: API, entetes: getAuthHeaders(tenantId) });
    if (ok) { toast.success("Dossier mis de côté — on le retrouve dans « Mis de côté »"); onBack?.(); }
    else toast.error("Impossible de mettre le dossier de côté");
  };

  // Statut change handler
  const handleStatutChange = async (newStatut, raison) => {
    try {
      const res = await fetch(`${API}/api/metier/dossiers/${dossier.id || dossierId}/statut`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...(localStorage.getItem("session_token") && { Authorization: `Bearer ${localStorage.getItem("session_token")}` }) },
        credentials: "include",
        body: JSON.stringify(raison ? { statut: newStatut, raison } : { statut: newStatut })
      });
      if (res.ok) { toast.success(`Statut mis à jour`); loadDossier(); }
      else { const e = await res.json(); toast.error(e.detail || "Erreur"); }
    } catch { toast.error("Erreur réseau"); }
  };

  // OH-0012 — bascule HUMAINE Actif/Inactif (indépendante du statut). Un dossier
  // "Inactif" matérialise une décision externe (ex. annulation DDT) que l'ANAH ne
  // reflète pas : il est GELÉ par la synchro (statut non écrasé, non réenrichi).
  const [togglingActif, setTogglingActif] = useState(false);
  const handleToggleActif = async () => {
    const next = (dossier.actif_inactif === "Inactif") ? "Actif" : "Inactif";
    setTogglingActif(true);
    try {
      const res = await fetch(`${API}/api/metier/dossiers/${dossier.id || dossierId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...(localStorage.getItem("session_token") && { Authorization: `Bearer ${localStorage.getItem("session_token")}` }) },
        credentials: "include",
        body: JSON.stringify({ actif_inactif: next }),
      });
      if (res.ok) { toast.success(next === "Inactif" ? "Dossier marqué Inactif (gelé pour la synchro ANAH)" : "Dossier réactivé"); loadDossier(); }
      else { const e = await res.json().catch(() => ({})); toast.error(e.detail || "Erreur"); }
    } catch { toast.error("Erreur réseau"); }
    finally { setTogglingActif(false); }
  };

  // ─── Loading / Error ───
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--oh-text-muted)" }} />
        <p className="text-sm" style={{ color: "var(--oh-text-muted)" }}>Chargement du dossier...</p>
      </div>
    );
  }

  if (!dossier) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto text-[var(--oh-error-text)] mb-4" />
        <p style={{ color: "var(--oh-text)" }}>Dossier non trouvé</p>
        <div className="flex gap-2 justify-center mt-4">
          <Button variant="outline" onClick={onBack}><ArrowLeft className="w-4 h-4 mr-2" /> Retour</Button>
          <Button variant="outline" onClick={loadDossier}><RefreshCw className="w-4 h-4 mr-2" /> Réessayer</Button>
        </div>
      </div>
    );
  }

  // ─── VUES DÉDIÉES PAR RÔLE ───
  if (effectiveRole === "installateur") {
    return <DossierInstallateurView dossier={dossier} dossierId={dossierId} tenantId={tenantId} documents={documents} onBack={onBack} onUpdate={loadDossier} />;
  }
  if (effectiveRole === "visiteur") {
    return <DossierVisiteurView dossier={dossier} dossierId={dossierId} tenantId={tenantId} documents={documents} onBack={onBack} onUpdate={loadDossier} />;
  }

  // ─── RÉSOLUTION TENANT EXPLICITE — plus jamais de défaut TEF implicite ───
  // Un dossier mal scopé (tenant_id absent ET contexte non résolu) doit être VISIBLE
  // (état vide explicite), pas déguisé en fiche TEF. Signaux par ordre d'autorité :
  //   1. dossier.tenant_id  (stampé en base — autoritaire ; cf. fix stamp omv_france Dev 2)
  //   2. tenantId           (tenant actif = currentEntreprise.slug)
  //   3. current_tenant_slug (même source que HubGrid — évite la désync prop/localStorage)
  let resolvedTenant = dossier.tenant_id || tenantId || null;
  if (!resolvedTenant && typeof window !== "undefined") {
    try { resolvedTenant = localStorage.getItem("current_tenant_slug") || null; } catch { /* noop */ }
  }

  // Profil installateur (OMV, Climat) → fiche CEE dédiée (DossierOmvView).
  if (isInstallateurProfile()) {
    return (
      <DossierOmvView
        dossier={dossier} dossierId={dossierId} tenantId={tenantId} documents={documents}
        historique={historique}
        onBack={onBack} onUpdate={loadDossier} onNavigate={onNavigate}
        onStatutChange={handleStatutChange} onDelete={isTEFRole ? handleDeleteDossier : undefined}
        isTEFRole={isTEFRole}
      />
    );
  }

  // 🔴 L2 (N°876) — un tenant qui a ADOPTÉ le modèle Site (un dossier = 1 client
  // + 1 site) reçoit la FICHE ARTISAN dédiée, ⛔ plus la fiche TEF filtrée par
  // gates. ⛔ Aucun composant du DAL gelé n'y est branché. TEF, OMV et Climat
  // ne déclarent pas `dossier_site_requis` ⇒ ⛔ inchangés.
  if (hasDossierSite()) {
    return (
      <DossierArtisanView
        dossier={dossier} dossierId={dossierId} tenantId={tenantId} documents={documents}
        historique={historique}
        onBack={onBack} onUpdate={loadDossier} onNavigate={onNavigate}
      />
    );
  }

  // 🔴 LA GARDE VÉRIFIE LE RATTACHEMENT, ⛔ PLUS UNE LISTE DE SLUGS.
  //
  // ⚠️ CE QU'ELLE FAISAIT, mesuré le 2026-09-17 : elle comparait le tenant à
  // ["tef", "climatpac", "demo"] — une LISTE BLANCHE EN DUR, dont le
  // commentaire disait « ajuster la liste si besoin ». Résultat : JJ crée
  // MARI-DOS-2026-00003, le document est PARFAITEMENT rattaché
  // (tenant_id: "mario_bat"), et la fiche REFUSE de s'afficher — au motif
  // « non rattaché à un tenant reconnu », qui est FAUX.
  // ⛔ Sans fiche : pas de visite, pas de dictée, pas de devis.
  //
  // 🔑 CE QUE LA GARDE DOIT VRAIMENT EMPÊCHER, et qu'elle empêche TOUJOURS :
  // qu'un dossier SANS tenant résolu soit déguisé en fiche TEF. Ça se teste
  // sur l'ABSENCE de rattachement — ⛔ pas sur l'appartenance à une liste que
  // chaque nouveau tenant oblige à rééditer. Les profils ayant une fiche
  // DIFFÉRENTE (OMV/Climat) sont déjà aiguillés AU-DESSUS.
  // ⚠️ Même famille que `!isOmv`, en miroir : la liste était POSITIVE et
  // FERMÉE, donc elle excluait en silence tout arrivant. ⛔ On ⛔ n'ajoute PAS
  // « mario_bat » : le tenant suivant retomberait dans le même mur.
  if (!resolvedTenant) {
    return (
      <div className="text-center py-12" data-testid="dossier-tenant-inconnu">
        <AlertTriangle className="w-12 h-12 mx-auto mb-4" style={{ color: "var(--oh-stat-amber-text)" }} />
        <p className="font-medium" style={{ color: "var(--oh-text)" }}>Dossier non rattaché à un tenant reconnu</p>
        <p className="text-sm mt-1 max-w-md mx-auto" style={{ color: "var(--oh-text-muted)" }}>
          Tenant résolu : <strong>{resolvedTenant || "aucun"}</strong> — fiche non affichée par sécurité
          (pas de repli TEF). Vérifier le rattachement du dossier{" "}
          <strong>{dossier.reference || dossier.référence || dossierId}</strong>.
        </p>
        <div className="flex gap-2 justify-center mt-4">
          <Button variant="outline" onClick={onBack}><ArrowLeft className="w-4 h-4 mr-2" /> Retour</Button>
          <Button variant="outline" onClick={loadDossier}><RefreshCw className="w-4 h-4 mr-2" /> Réessayer</Button>
        </div>
      </div>
    );
  }

  // ─── VUE GÉNÉRIQUE (tenant explicite : tef / climatpac / demo) — inchangée ───
  // PN-7 : libellé = liste du tenant (sinon libellé de l'étape du pipeline, sinon libellé lisible : jamais le code brut).
  // La couleur reste la paire de la table historique quand le code y figure, sinon une paire neutre à contraste garanti.
  const statutsMap = STATUTS_CONFIG[dossier.activite] || STATUTS_CONFIG.tef;
  const abandoned = isAbandonne(dossier);
  const groupesMenu = groupesStatuts(statutsCfg, dossier.activite, pipeline, dossier.statut);   // T-430b : rangés par étape du pipeline
  const statutsMenu = groupesMenu.flatMap((g) => g.entrees);
  const statutHist = statutsMap[dossier.statut];

  const statutInfo = abandoned
    ? { label: "Abandonné", color: "bg-[var(--oh-error-bg)] border border-[var(--oh-error-border)] text-[var(--oh-error-text)] dark:text-[var(--oh-error-text)]", icon: AlertTriangle }
    : { label: libelleStatut(dossier.statut, statutsCfg, dossier.activite, pipeline),
        color: statutHist?.color || "bg-zinc-500/20 border border-[var(--oh-border)] text-[var(--oh-text)] dark:text-[var(--oh-text-secondary)]",
        icon: statutHist?.icon || FileText };
  const StatusIcon = statutInfo.icon;
  // PN-6 : code d'étape du PIPELINE (cree, visite_effectuee, en_cours_de_montage…), SANS repli vers « cree ». Avant, getPipelineStep était appelé SANS pipeline :
  // il rendait toujours « cree », et ni le bouton d'en-tête ni les encadrés « Prochaine étape » ne s'affichaient jamais.
  // Pipeline pas encore chargé ou statut non mappé : null (aucune action proposée, jamais de repli vers « cree »).
  const pipelineStep = etapeDuDossier(dossier.statut, dossier, pipeline);
  const chantierVisible = afficherChantier(dossier, pipelineStep);   // PN-11 : même condition pour le bloc et pour son entrée de menu
  const completudePct = dossier.completude_agent?.global_pct || 0;
  // PN-11 : les compteurs par onglet dérivent de l'indicateur UNIQUE (completude_agent), le même que le pourcentage de l'en-tête.
  const tabCompletude = completudeParOnglet(dossier.completude_agent);
  const dossierRef = dossier.reference || dossier.référence || "";
  // P0-04 fix BUG-11 (2026-05-03) — formatClientFullName gere aussi le cas B2B (raison_sociale)
  const clientDisplay = formatClientFullName(dossier.client) || dossier.client_nom || "—";
  // Refonte P1.1 — méta header condensé : ville · visiteur · installateur (mis en avant).
  // Mêmes sources que AdminBlock (adresse_projet / visiteur_amo / installateur_nom),
  // pas de nouvelle source ni de hard code (O1).
  const headerVille = dossier.adresse_projet?.ville || dossier.logement?.ville || dossier.client?.adresse?.ville || "";
  const headerCP = dossier.adresse_projet?.code_postal || dossier.logement?.code_postal || dossier.client?.adresse?.code_postal || "";
  // BAT JJ — ville AVEC code postal (ex. "Bobigny 93000"), mêmes sources que AdminBlock.
  const headerLocalite = [headerVille, headerCP].filter(Boolean).join(" ");
  const headerVisiteur = dossier.visiteur_amo || "";
  const headerInstallateur = dossier.installateur_nom || "";
  // Indicateur conformité = complétude de l'onglet Conformité déjà calculée (réutilisée, pas recalculée).
  const headerConf = tabCompletude.conformite;

  // Header action contextuelle (PN-6/PN-9) — lue dans dossier.prochaine_etape (serveur) : la MÊME fonction que la colonne « Prochaine action » des grilles.
  const headerAction = abandoned ? null : prochaineEtapeFiche(dossier, pipelineStep, visibleTabs.map((t) => t.key));
  const phraseEtape = phraseOuEnEst(pipeline, pipelineStep, abandoned, dossier);

  return (
    <div className="space-y-0" data-testid="dossier-detail">

      {/* ═══ HEADER STICKY ═══ */}
      <div
        className="sticky top-0 z-40 -mx-4 sm:-mx-0 backdrop-blur-xl"
        style={{ background: "var(--oh-bg-elevated)", borderBottom: "1px solid var(--oh-border)" }}
        data-testid="dossier-header"
      >
        {/* Row 1: Nav + Client + Statut + Complétude + Actions */}
        <div className="flex items-center gap-2 sm:gap-3 px-4 py-2.5 flex-wrap">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-btn" className="p-1.5">
            <ArrowLeft className="w-4 h-4" style={{ color: "var(--oh-text-muted)" }} />
          </Button>

          {/* Client name */}
          <div className="flex items-center gap-2 min-w-0">
            <div className={`p-1 rounded ${amo ? "bg-[var(--oh-accent-bg)]" : "bg-[var(--oh-info-bg)]"}`}>
              {amo ? <Building2 className="w-3.5 h-3.5 text-[var(--oh-accent-text)]" /> : <Thermometer className="w-3.5 h-3.5 text-[var(--oh-info-text)]" />}
            </div>
            <div className="min-w-0">
              {/* Header principal : REF — NOM (demande JJ 2026-04-21 pour
                  appels ANAH/client où la ref est la clé de recherche) */}
              <p className="text-sm font-medium truncate flex items-center gap-1.5" style={{ color: "var(--oh-text)" }} data-testid="dossier-header-title">
                {dossierRef && (
                  <>
                    <span className="font-mono" style={{ color: "var(--oh-text-muted)" }}>
                      {dossierRef}
                    </span>
                    <button
                      onClick={copyRef}
                      className="p-0.5 rounded hover:bg-[var(--oh-bg-elevated)] transition-colors"
                      title={refCopied ? "Copié !" : "Copier la référence"}
                      data-testid="copy-ref-btn"
                    >
                      {refCopied
                        ? <Check className="w-3 h-3 text-[var(--oh-success-text)]" />
                        : <Copy className="w-3 h-3" style={{ color: "var(--oh-text-muted)" }} />}
                    </button>
                    <span style={{ color: "var(--oh-text-muted)" }}>— </span>
                  </>
                )}
                {clientDisplay}
              </p>
              {/* Refonte P1.1 — méta condensée : ville · visiteur · INSTALLATEUR (mis en avant) */}
              <div className="hidden sm:flex items-center gap-2 text-[11px] mt-0.5 flex-wrap" data-testid="dossier-header-meta" style={{ color: "var(--oh-text-muted)" }}>
                {headerLocalite && (
                  <span className="inline-flex items-center gap-1" data-testid="header-ville">
                    <MapPin className="w-3 h-3" /> {headerLocalite}
                  </span>
                )}
                {headerVisiteur && (
                  <span className="inline-flex items-center gap-1" data-testid="header-visiteur">
                    <span style={{ opacity: 0.5 }}>·</span> <Briefcase className="w-3 h-3" /> {headerVisiteur}
                  </span>
                )}
                {/* 🔴 N°728-6 — LE BADGE D'EN-TÊTE ÉTAIT INCONDITIONNEL. ⚠️ Le
                    bloc « Installateur » de l'onglet Opération est gaté depuis le
                    N°690-2, et l'entrée de sous-menu aussi — mais ce badge, en
                    HAUT de chaque dossier, affichait « Installateur — » à un
                    artisan qui pose lui-même. Un mot d'un autre métier, sur
                    l'écran le plus vu. MÊME capacité que le bloc. */}
                {installateur && (
                <span className="inline-flex items-center gap-1">
                  <span style={{ opacity: 0.5 }}>·</span>
                  <span
                    className="inline-flex items-center gap-1 font-medium px-1.5 py-0.5 rounded"
                    data-testid="header-installateur"
                    title={headerInstallateur ? `Installateur : ${headerInstallateur}` : "Aucun installateur assigné"}
                    style={headerInstallateur
                      ? { background: "var(--oh-stat-blue-bg)", color: "var(--oh-stat-blue-text)", border: "1px solid var(--oh-stat-blue-border)" }
                      : { color: "var(--oh-text-muted)", border: "1px dashed var(--oh-border)" }}
                  >
                    <HardHat className="w-3 h-3" /> {headerInstallateur || "Installateur —"}
                  </span>
                </span>
                )}
              </div>
            </div>
          </div>

          {/* Statut : un clic ouvre la boîte « Changer le statut » (liste du tenant, raison demandée pour les sorties du parcours) */}
          <button className="flex items-center gap-1 cursor-pointer hover:opacity-80" data-testid="statut-dropdown-trigger"
            onClick={() => setShowChangerStatut(true)} aria-label={`Statut : ${statutInfo.label}. Changer le statut`}>
            <Badge className={`${statutInfo.color} px-2 py-0.5 text-xs`}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {statutInfo.label}
              <ChevronDown className="w-3 h-3 ml-1 opacity-50" />
            </Badge>
          </button>
          <ChangerStatutDialog ouvert={showChangerStatut} onFermer={() => setShowChangerStatut(false)} statuts={statutsMenu} groupes={groupesMenu}
            statutActuel={dossier.statut} onConfirm={(code, raison) => handleStatutChange(code, raison)} />

          {/* Complétude */}
          {completudePct > 0 && (
            <div className="hidden sm:flex items-center gap-1.5" data-testid="completude-header">
              <div className="w-16 h-1.5 rounded-full" style={{ background: "var(--oh-border)" }}>
                <div className="h-full rounded-full transition-all" style={{
                  width: `${completudePct}%`,
                  background: completudePct >= 80 ? "#10b981" : completudePct >= 50 ? "var(--oh-stat-amber-text)" : "var(--oh-stat-red-text)",
                }} />
              </div>
              <span className="text-[10px] font-medium" style={{
                color: completudePct >= 80 ? "#10b981" : completudePct >= 50 ? "var(--oh-stat-amber-text)" : "var(--oh-stat-red-text)",
              }}>{completudePct}%</span>
            </div>
          )}

          {/* Refonte P1.1 — indicateur conformité (réutilise la complétude de l'onglet Conformité) */}
          {headerConf && (
            <div
              className="hidden sm:flex items-center gap-1"
              data-testid="conformite-header"
              title={headerConf.missing?.length ? `Conformité à compléter : ${headerConf.missing.join(", ")}` : "Conformité complète ✓"}
            >
              <Shield className="w-3 h-3" style={{ color: headerConf.filled === headerConf.total ? "#10b981" : "var(--oh-stat-amber-text)" }} />
              <span className="text-[10px] font-medium" style={{ color: headerConf.filled === headerConf.total ? "#10b981" : "var(--oh-stat-amber-text)" }}>
                {headerConf.filled}/{headerConf.total}
              </span>
            </div>
          )}

          {dossier.verrouille && (
            <Badge className="bg-[var(--oh-error-bg)] text-[var(--oh-error-text)] border-[var(--oh-error-border)] text-[10px]"><Lock className="w-3 h-3 mr-0.5" />Verrouillé</Badge>
          )}
          {/* PN-11 : le gel (Actif/Inactif) se pilote dans « Outils avancés », mais l'ÉTAT reste visible de tous : un dossier gelé n'est plus mis à jour par l'ANAH */}
          {dossier.actif_inactif === "Inactif" && (
            <span data-testid="badge-dossier-gele" className="text-[10px] font-medium rounded px-2 py-0.5 inline-flex items-center gap-1"
              title="Ce dossier est gelé : la synchronisation ANAH ne le modifie plus."
              style={{ background: "var(--oh-stat-amber-bg)", color: "var(--oh-stat-amber-text)", border: "1px solid var(--oh-stat-amber-border)" }}>
              <Lock className="w-3 h-3" />Dossier gelé
            </span>
          )}

          <div className="flex-1" />

          {/* Action principale contextuelle */}
          {headerAction && !abandoned && !headerAction.tab && (
            <span className="text-xs inline-flex items-center h-7 px-3 rounded-md" data-testid="header-action-label" title={headerAction.motif || undefined}
              style={{ background: "var(--oh-stat-blue-bg)", color: "var(--oh-stat-blue-text)", border: "1px solid var(--oh-stat-blue-border)" }}>
              <ArrowRight className="w-3 h-3 mr-1" />
              {headerAction.label}
            </span>
          )}
          {headerAction && !abandoned && headerAction.tab && (
            <Button size="sm" variant="outline" data-testid="header-action-btn" title={headerAction.motif || undefined}
              className="text-xs h-7 px-3 !text-[var(--oh-accent-text)]"
              style={{ background: "transparent", border: "1px solid var(--oh-accent)" }}
              onClick={() => setActiveTab(headerAction.tab)}>
              <ArrowRight className="w-3 h-3 mr-1" />
              {headerAction.label}
            </Button>
          )}

          {/* Étape externe assumée : le dépôt/suivi réel se fait sur le portail
              officiel ANAH (monprojet.anah.gouv.fr). Lien secondaire, non impératif. */}
          {headerAction?.anahPortal && !abandoned && (
            <a href="https://monprojet.anah.gouv.fr" target="_blank" rel="noopener noreferrer"
              data-testid="anah-portal-link"
              className="inline-flex items-center text-xs h-7 px-3 rounded-md whitespace-nowrap"
              style={{ background: "transparent", color: "var(--oh-accent-text)", border: "1px solid var(--oh-accent)" }}
              title="Ouvre le portail officiel ANAH dans un nouvel onglet">
              <ExternalLink className="w-3 h-3 mr-1" />
              Continuer sur le portail officiel ANAH
            </a>
          )}

          <GovernanceIndicator dossierId={dossierId} tenantId={tenantId} onBlockedTransitions={setBlockedTransitions} />
          {/* BAT JJ — barre d'actions déplacée sur sa propre ligne pleine largeur (Row 1-bis ci-dessous). */}
        </div>

        {/* Row 1-bis — barre d'actions NOMMÉES sur sa propre ligne pleine largeur (BAT JJ :
            plus de boutons tassés/coupés à droite). Ordre : Documents · Commentaires · Message ·
            Historique · Sync Zoho · Analyse IA · Aide · Supprimer. Wrap propre, labels dès sm. */}
        <div className="flex items-center gap-1.5 px-4 pb-2 flex-wrap" data-testid="dossier-actions-bar"
          style={{ borderTop: "1px solid var(--oh-border)", paddingTop: "0.5rem" }}>
          <Button data-testid="documents-btn" size="sm" variant="ghost" className="text-xs h-7 px-2 gap-1.5"
            onClick={() => setShowDocuments(true)} title="Documents du dossier" aria-label="Documents">
            <Files className="w-3.5 h-3.5" style={{ color: "var(--oh-text-muted)" }} />
            <span className="hidden sm:inline">Documents{documents.length > 0 ? ` (${documents.length})` : ""}</span>
          </Button>
          <Button data-testid="commentaires-btn" size="sm" variant="ghost" className="text-xs h-7 px-2 gap-1.5"
            onClick={() => setShowCommentaires(true)} title="Commentaires du dossier" aria-label="Commentaires">
            <MessageSquare className="w-3.5 h-3.5" style={{ color: "var(--oh-text-muted)" }} />
            <span className="hidden sm:inline">Commentaires</span>
          </Button>
          <Button data-testid="send-message-btn" size="sm" variant="ghost" className="text-xs h-7 px-2 gap-1.5"
            onClick={() => setShowSendMessage(true)} title="Envoyer un message (SMS/email) au client" aria-label="Envoyer un message">
            <Send className="w-3.5 h-3.5" style={{ color: "var(--oh-text-muted)" }} />
            <span className="hidden sm:inline">Message</span>
          </Button>
          <Button data-testid="historique-btn" size="sm" variant="ghost" className="text-xs h-7 px-2 gap-1.5"
            onClick={() => setShowHistorique(true)} title="Historique du dossier" aria-label="Historique">
            <Clock className="w-3.5 h-3.5" style={{ color: "var(--oh-text-muted)" }} />
            <span className="hidden sm:inline">Historique</span>
          </Button>
          {(user?.role || user?.rôle) !== "visiteur" && (
            <Button data-testid="btn-aide-contextuelle" size="sm" variant="ghost" className="text-xs h-7 px-2 gap-1.5"
              onClick={() => setShowTuto(true)} title="Aide contextuelle" aria-label="Aide">
              <HelpCircle className="w-3.5 h-3.5" style={{ color: "var(--oh-text-muted)" }} />
              <span className="hidden sm:inline">Aide</span>
            </Button>
          )}
          {isTEFRole && (
            <Button data-testid="mettre-de-cote-btn" size="sm" variant="ghost" className="text-xs h-7 px-2 gap-1.5"
              onClick={() => setShowMettreDeCote(true)} title="Mettre le dossier de côté : rien n'est effacé, la raison est gardée" aria-label="Mettre de côté">
              <Archive className="w-3.5 h-3.5" style={{ color: "var(--oh-text-muted)" }} />
              <span className="hidden sm:inline">Mettre de côté</span>
            </Button>
          )}
          {/* Outils avancés (super admin seulement) : la suppression DÉFINITIVE n'est plus dans la barre de tous les jours */}
          <OutilsAvancesMenu entrees={estSuperAdmin ? [
            amo && { id: "sync-zoho", label: syncingZoho ? "Synchronisation Zoho en cours…" : "Resynchroniser les fichiers Zoho", icon: FolderOpen, disabled: syncingZoho, onClick: handleResyncZoho },
            amo && !abandoned && { id: "analyse-ia", label: analyzingAgent ? "Analyse en cours…" : "Lancer l'analyse IA de conformité", icon: Shield, disabled: analyzingAgent, onClick: handleLaunchAgent },
            isTEFRole && { id: "gel-synchro-anah",
              label: dossier.actif_inactif === "Inactif" ? "Réactiver le dossier (dégeler la synchro ANAH)" : "Geler la synchro ANAH pour ce dossier",
              icon: Lock, disabled: togglingActif, onClick: handleToggleActif },
            hasIntegrationPennylane() && { id: "synchro-pennylane", label: "Voir l'état de la synchro Pennylane", icon: CreditCard, onClick: () => setShowSynchroPennylane(true) },
            { id: "supprimer-definitivement", label: "Supprimer définitivement (irréversible)", icon: Trash2, danger: true, onClick: handleDeleteDossier },
          ] : []} />
          <SynchroPennylaneDialog ouvert={showSynchroPennylane} onFermer={() => setShowSynchroPennylane(false)} dossier={dossier} />
          <MettreDeCoteDialog ouvert={showMettreDeCote} onFermer={() => setShowMettreDeCote(false)} onConfirm={handleMettreDeCote} />
        </div>

        {/* Agent progress banner */}
        {analyzingAgent && agentProgress && (
          <div className="flex items-center gap-3 px-4 py-1.5 text-xs" style={{ background: "rgba(59,130,246,0.08)", borderTop: "1px solid rgba(59,130,246,0.15)" }}>
            <Loader2 className="w-3 h-3 animate-spin text-[var(--oh-accent-text)]" />
            <span style={{ color: "var(--oh-stat-blue-text)" }}>{agentProgress}</span>
          </div>
        )}

        {/* Row 2: Pipeline Stepper */}
        <div className="px-4 py-1.5">
          <PipelineStepper statut={dossier.statut} compact dossier={dossier} />
          {/* PN-6 — une phrase : où en est le dossier et ce qui vient ensuite (règle « OmniHub pour les nuls ») */}
          {phraseEtape && (
            <p className="text-xs mt-1" style={{ color: "var(--oh-text-secondary)" }} data-testid="dossier-ou-en-est">{phraseEtape}</p>
          )}
        </div>

        {/* 🔴 ETI-101 — porte du DOSSIER, si le tenant en déclare une (sinon RIEN
            n'est rendu, pas même un espace). L'étape suivante = le geste EXISTANT
            de création de devis ; le serveur garde (`exiger_porte_franchie`). */}
        <PorteDecision
          className="mx-4 my-1.5"
          entiteType="dossier"
          entiteId={dossier.id || dossierId}
          tenantId={tenantId}
          role={userRole}
          actionSuivante={onNavigate ? {
            label: "Créer le devis",
            onClick: () => onNavigate("devis-wizard", { dossierId, devisId: "new" }),
          } : undefined}
          onDecided={loadDossier}
        />

        {/* Row 2-bis : Dates par statut pipeline (S2 2026-05-28, collapsible) */}
        <details className="px-4 py-1.5" data-testid="dates-pipeline-collapsible">
          <summary className="text-xs cursor-pointer select-none" style={{ color: "var(--oh-text-muted)" }}>
            Dates par étape du dossier
          </summary>
          <div className="mt-2 max-w-2xl">
            <DatesPipelineBlock
              dossier={dossier}
              tenantId={tenantId}
              readOnly={!isTEFRole}
              onUpdated={loadDossier}
            />
          </div>
        </details>

        {/* Row 3: Onglets */}
        <div className="flex px-4 gap-0 overflow-x-auto" data-testid="dossier-tabs">
          {visibleTabs.map(tab => {
            const isActive = activeTab === tab.key;
            const TabIcon = tab.icon;
            const tc = tabCompletude[tab.key];
            const isComplete = tc && tc.filled === tc.total;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                data-testid={`tab-${tab.key}`}
                title={tc ? (tc.missing?.length ? `À compléter : ${tc.missing.join(", ")}` : "Complet ✓") : undefined}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium whitespace-nowrap transition-all relative"
                style={{
                  color: isActive ? "var(--oh-text)" : "var(--oh-text-muted)",
                  borderBottom: isActive ? `3px solid ${tab.color}` : "3px solid transparent",
                }}
              >
                <TabIcon className="w-4 h-4" style={{ color: isActive ? tab.color : undefined }} />
                <span className="hidden sm:inline">{(!amo && tab.labelCore) ? tab.labelCore : tab.label}</span>
                <span className="sm:hidden">{((!amo && tab.labelCore) ? tab.labelCore : tab.label).slice(0, 5)}</span>
                {tc && (
                  <span
                    className="text-[10px] font-mono font-medium px-1.5 py-0.5 rounded-md"
                    data-testid={`tab-completude-${tab.key}`}
                    style={{
                      background: isComplete ? "rgba(16,185,129,0.2)" : isActive ? `${tab.color}20` : "rgba(148,163,184,0.1)",
                      color: isComplete ? "#10b981" : isActive ? tab.color : "var(--oh-text-muted)",
                      border: `1px solid ${isComplete ? "rgba(16,185,129,0.3)" : isActive ? `${tab.color}30` : "transparent"}`,
                    }}
                  >
                    {tc.filled}/{tc.total}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Refonte P1 #3 : ce qui manque dans l'onglet actif (affichage clair) */}
        {(() => {
          const tcActive = tabCompletude[activeTab];
          if (!tcActive || !tcActive.missing?.length) return null;
          return (
            <div
              className="mx-4 mb-2 rounded-md px-3 py-1.5 flex items-center gap-2 text-xs"
              data-testid="tab-missing-hint"
              style={{
                background: "var(--oh-stat-amber-bg, rgba(245,158,11,0.1))",
                border: "1px solid var(--oh-stat-amber-border, rgba(245,158,11,0.25))",
                color: "var(--oh-stat-amber-text)",
              }}
            >
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
              <span>À compléter : <strong>{tcActive.missing.join(", ")}</strong></span>
            </div>
          );
        })()}
      </div>

      {/* Refonte P1.1 — AdminBlock (Bénéficiaire + Logement) n'est PLUS répété sur
          tous les onglets : déplacé dans l'onglet Infos uniquement (détail bénéficiaire).
          Le header condensé porte désormais ville·visiteur·installateur·conformité. */}

      {/* ═══ CONTENU ONGLET ═══ */}
      {/* PROTOTYPE SPLIT VIEW (design JJ) — les 5 onglets (Infos / Demande MPA /
          DAL / Finance / Conformité) sont désormais TOUS en master-detail :
          le frame s'étale PLEINE LARGEUR (plus de max-w-[1100px]/mx-auto),
          on garde juste un padding latéral de respiration. */}
      <div className="pt-4 space-y-4 w-full px-3 sm:px-5 lg:px-6">

        {/* ═══ ONGLET INFOS ═══ */}
        {activeTab === "infos" && (
          <div className="space-y-4" data-testid="tab-content-infos">
            {/* OMV France : fiche dédiée gérée par DossierOmvView (early-return ci-dessus),
                ne passe jamais par cet onglet TEF. */}

            {/* Onglet Infos refondu (spec docs/upload/mockup-infos.html). Blocs :
                Bénéficiaire / Logement / MaPrimeAdapt' / Compte ANAH.
                SPLIT VIEW (design JJ) : InfosTab est rendu TEL QUEL ; ses 4 BlockCards
                sont ancrés data-subnav-id, le menu gauche les montre un à un. Le
                toprow « Remplir depuis documents » + la note IA restent persistants. */}
            <SplitViewLayout sections={SPLIT_SECTIONS_INFOS.filter(x => amo || !["mpa", "compte-anah"].includes(x.id)).map(x => (!amo && x.id === "beneficiaire") ? { ...x, label: "Client" } : x)} scanKey="infos" ariaLabel="Sections Infos">
              <InfosTab dossier={dossier} dossierId={dossierId} tenantId={tenantId} onUpdate={loadDossier} />
            </SplitViewLayout>

            {/* #5 (2026-06-21) — bloc « Documents & Photos » embarqué RETIRÉ d'Infos :
                doublon du bouton header « Documents » → panneau (qui couvre désormais
                Documents ET Photos, dépose + consulte). Accès conservé, zéro perte.
                Les sous-onglets métier Pièces/Préparer&Signer/Documents générés
                (DocumentsAidesWrapper, onglet Demande MPA) sont CONSERVÉS (fonction
                propre : génération + signature, distincte du simple dépôt). */}

            {/* CommercialSection déplacé vers l'onglet « Demande MPA » (Refonte P1.1, commit 2/3). */}
          </div>
        )}

        {/* ═══ ONGLET DEMANDE MPA (clé interne "operation") — Contrats & Engagement + Opération ═══ */}
        {activeTab === "operation" && (
          <div className="space-y-2.5" data-testid="tab-content-operation">
            {/* CC-DEV1-17 #1 — « À compléter » déplacé ici depuis l'onglet Infos
                (ACompleterBlock = source unique de la logique). Persistant au-dessus
                du split (guidance transversale, non rattaché à une section). */}
            <ACompleterBlock dossier={dossier} checklist={checklistAnah} />

            {/* 🔴 N°601 delta 1 — LE PONT DOSSIER → VISITE. Gaté par la
                CAPABILITY `operation.visites` (⛔ pas par le slug du tenant) :
                le bloc DAL de TEF ⛔ n'est PAS touché. */}
            <EnvoyerVisiteBlock dossierId={dossierId} dossier={dossier} />

            {/* PROTOTYPE SPLIT VIEW (design JJ) — master-detail intra-onglet :
                menu sections à GAUCHE + contenu à DROITE. AUCUN contenu recréé :
                CommercialSection + OperationSection sont rendus tels quels, leurs
                blocs `data-subnav-id` (contrats / prestation-amo / visiteur /
                visite / installateur / financement / chantier) sont montrés un à un. */}
            {/* 🔴🔴 N°648 — LE GATE ÉTAIT SUR UN CONTENEUR, ⛔ PAS SUR CE QU'IL
                DEVAIT CACHER. Mon N°637/N°640 filtrait la section « contrats »
                ENTIÈRE quand `hasDossierAmo()` est faux. Or ce conteneur héberge
                AUSSI « Devis & Factures » (CommercialSection, `contrats-cta-top`)
                ⇒ ⛔ MARIO N'AVAIT PLUS ACCÈS À SES DEVIS EN PRODUCTION.
                🔑 LA LEÇON : un gate se pose sur LE BLOC qu'il doit cacher, ⛔
                jamais sur un parent dont on n'a pas inventorié le contenu. Le
                gate AMO vit désormais DANS CommercialSection, sur le seul bloc
                contrat AMO / mandat / mandataire. « prestation-amo », lui, est
                un bloc À LUI SEUL : son filtre ici reste juste. */}
            <SplitViewLayout sections={SPLIT_SECTIONS_OPERATION.filter(x => (chantierVisible || x.id !== "chantier") && (amo || x.id !== "prestation-amo") && (installateur || x.id !== "installateur") && (installateur || x.id !== "visite"))} scanKey={`op-${dossier.statut}-${chantierVisible}`} ariaLabel={amo ? "Sections Demande MPA" : "Sections Opération"}>
              {/* Refonte P1.1 — Contrats & Engagement (Devis/Contrat AMO + choix visiteur + DocuSign) :
                  CommercialSection déplacé ENTIER depuis l'onglet Infos (zéro re-découpage interne). */}
              {/* 🔴🔴 N°752-3 — DEUX FOIS J'AI CRU CE PROP MORT, DEUX FOIS C'ÉTAIT
                  FAUX, et mon banc l'a attrapé les deux fois. `CommercialSection`
                  LIT `isTEF` CINQ fois — dont l.61 le CHOIX DE L'ENDPOINT
                  (`donnees-mpa` vs `donnees-mpr`) : sans le prop, TEF partait
                  chercher ses données sur la route de MaPrimeRénov'.
                  ⚠️ Un `grep` sur plusieurs fichiers d'un coup, lu trop vite, m'a
                  fait conclure « 0 occurrence ». Le BUILD ⛔ ne voit RIEN (un prop
                  absent vaut `undefined` en JSX). ⇒ On passe la CAPACITÉ, et les
                  deux sections gardent leur comportement — à l'identique pour TEF. */}
              <CommercialSection dossier={dossier} dossierId={dossierId} tenantId={tenantId} isTEF={amo} abandoned={abandoned} onUpdate={loadDossier} onNavigate={onNavigate} />

              {/* 🔴🔴 N°752-3 — ⚠️ J'AI FAILLI RETIRER CE PROP AUSSI, et c'était une
                  RÉGRESSION : mon premier grep couvrait les deux fichiers d'un
                  coup et je n'ai lu que les résultats du voisin. `OperationSection`
                  LIT `isTEF` trois fois (l.184 : le fetch de cohérence DAL ;
                  l.196 : sa dépendance ; l.698 : l'alerte d'écart). Sans le prop,
                  la valeur devenait `undefined` ⇒ le fetch ⛔ ne partait PLUS et
                  l'alerte ⛔ ne s'affichait PLUS, chez TEF. Attrapé par mon propre
                  banc, ⛔ pas par le build (JSX : un prop manquant ⛔ ne casse rien).
                  ⇒ Il reçoit la CAPACITÉ. Seul `CommercialSection` perd le prop :
                  vérifié fichier par fichier, il ⛔ n'a AUCUN usage de `isTEF`. */}
              <OperationSection dossier={dossier} dossierId={dossierId} tenantId={tenantId} isTEF={amo} abandoned={abandoned} onUpdate={loadDossier}>
                {/* Chantier — rendu UNIQUEMENT en phase travaux. Le div lui-même est
                    conditionnel (et non un contenu vide dans un div toujours présent) :
                    ainsi SplitViewLayout ne détecte pas le bloc hors phase travaux et
                    RETIRE automatiquement l'entrée « Chantier » du menu (plus de section
                    vide). En phase travaux, la section réapparaît avec son contenu. */}
                {chantierVisible && (
                  <div data-subnav-id="chantier">
                    <div className="rounded-lg p-4" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}>
                      <PlanningChantier dossierId={dossier.id} tenantId={tenantId} dossier={dossier} />
                    </div>
                    <div className="rounded-lg p-4 mt-4" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}>
                      <CommandesManager dossierId={dossier.id} tenantId={tenantId} devisSigne={["signe","valide","accepte","en_commande","travaux_en_cours","travaux_termines"].includes(dossier.statut)} />
                    </div>
                    <PhotosChantier dossierId={dossier.id} tenantId={tenantId} verrouille={dossier.verrouille || abandoned} clientNom={dossier.client?.nom || ""} dossierRef={dossier.référence} />
                  </div>
                )}
              </OperationSection>
            </SplitViewLayout>

          </div>
        )}

        {/* ═══ ONGLET DAL (refonte P1 : sorti du <details> Conformité → visible) ═══ */}
        {activeTab === "dal" && amo && (
          <div className="space-y-4" data-testid="tab-content-dal">
            {/* PROTOTYPE SPLIT VIEW (design JJ) — master-detail intra-onglet DAL.
                AUCUN contenu recréé : DALMobileBlock / DesktopDALWizard (wizard
                partagé qui se navigue étape par étape À DROITE via son propre
                stepper) / DALForm (vue classique) sont rendus tels quels, chacun
                enveloppé d'un `data-subnav-id` pour le pilotage gauche/droite. */}
            <SplitViewLayout sections={SPLIT_SECTIONS_DAL} scanKey="dal" ariaLabel="Sections DAL" onNavigateRef={dalNavRef}>
              {/* CC-DEV1-17 #2 — DAL Mobile (saisie terrain). Fonction intacte. */}
              <div data-subnav-id="dal-mobile">
                <DALMobileBlock dossier={dossier} dossierId={dossierId} tenantId={tenantId} showEmptyState />
              </div>

              {/* CC-AUDIT-43 L2 — Assistant DAL = wizard PARTAGÉ (même composant que la
                  PWA mobile). Il conserve son propre stepper (Besoins → Zones →
                  Solutions → Préconisations → Générer) rendu à DROITE ; reprise de la
                  saisie via initialData (GET DAL), autosave session desktop. */}
              <div data-subnav-id="dal-wizard" data-testid="dal-wizard-section">
                <DesktopDALWizard dossier={dossier} dossierId={dossierId} tenantId={tenantId} onUpdate={loadDossier} />
              </div>

              <div data-subnav-id="dal-matthieu" data-testid="dal-matthieu-section">
                <RedigerAvecMatthieuPanel dossierId={dossierId} />
              </div>

              {/* "DAL" = CONSULTATION (V1.1) : tableau de bord LECTURE SEULE + barre
                  d'actions (Générer / Télécharger, pas d'Imprimer). La saisie se fait
                  dans "Assistant DAL". Source unique = même GET .../dal. DALForm reste
                  dans le code (filet) mais n'est plus monté ici. */}
              <div data-subnav-id="dal-classique" data-testid="dal-vue-classique">
                <DALConsultation dossierId={dossierId} reference={dossier?.reference} tenantId={tenantId} client={dossier?.client} infosLogement={dossier?.logement || dossier?.infos_logement} onUpdate={loadDossier} onEdit={() => dalNavRef.current?.("dal-wizard")} />
              </div>
            </SplitViewLayout>
          </div>
        )}

        {/* ═══ ONGLET CONFORMITÉ ═══ */}
        {activeTab === "visites" && (
          <VisitesDuDossier dossierId={dossierId} tenantId={tenantId} />
        )}

        {activeTab === "conformite" && (
          <div className="space-y-4" data-testid="tab-content-conformite">
            {/* SPLIT VIEW (design JJ) — 2 sections cohérentes : Liaison ANAH (trigger
                modale) + Vérification Conformité (ConformiteEditableTab ENTIER, qui
                garde son header/save/progress + ses accordéons internes Dates ANAH /
                Checklist pièces / Plan / Détail). Une découpe plus fine imposerait de
                recréer le header partagé du composant → refusé (O1). */}
            <SplitViewLayout sections={SPLIT_SECTIONS_CONFORMITE} scanKey="conformite" ariaLabel="Sections Conformité" onNavigateRef={conformiteNavRef}>
            {/* Liaison ANAH — données importées (lecture seule), INLINE dans le panneau
                droit du split (cohérence avec les autres sections ; plus de modale).
                Source unique ANAH inchangée : on réutilise tel quel AnahLinkTab +
                DonneesMPR (O1, même contenu que l'ancienne modale, rendu inline). */}
            {amo && (
              <div data-subnav-id="liaison-anah" className="rounded-lg overflow-hidden" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }} data-testid="anah-inline-section">
                <details open>
                  <summary className="flex items-center justify-between px-4 py-3 cursor-pointer select-none">
                    <span className="text-sm font-medium flex items-center gap-2" style={{ color: "var(--oh-text)" }}>
                      <Shield className="w-4 h-4 text-[var(--oh-success-text)]" /> Liaison ANAH
                      {(dossier?.anah_reference || dossier?.reference_anah)
                        ? <span className="text-xs font-normal" style={{ color: "var(--oh-text-muted)" }}>— Réf. {dossier.anah_reference || dossier.reference_anah}</span>
                        : <span className="text-xs font-normal" style={{ color: "var(--oh-text-muted)" }}>— non liée</span>}
                      {dossier?.statut_anah && <span className="text-xs font-normal" style={{ color: "var(--oh-text-muted)" }}>· statut {dossier.statut_anah}</span>}
                    </span>
                    <ChevronDown className="w-4 h-4" style={{ color: "var(--oh-text-muted)" }} />
                  </summary>
                  <div className="px-4 pb-4">
                    {/* Bloc « Dossier ANAH » (DonneesMPA) RETIRÉ (décision CTO) : doublon
                        avec Liaison ANAH, enrichi ci-dessus (numéro, statut, dates création/
                        dépôt/acceptation, montant accordé). Le mandataire se gère en Demande
                        MPA (liste) + Configuration Mandataire ANAH. */}
                    <AnahLinkTab dossierId={dossierId} tenantId={tenantId} readOnly={dossier?.verrouille || abandoned} />
                  </div>
                </details>
              </div>
            )}

            {/* Conformité editable — open par défaut : action principale de Stéphanie (dépôt ANAH) */}
            {/* Plan de financement MPA (CERFA 13460-03) — SECTION PROPRE du split
                Conformité (pièce du dépôt ANAH). Bouton Télécharger PERSISTANT +
                Régénérer (détection du doc généré via /devis-contrats). Sorti du slot
                planTrigger de ConformiteEditableTab où il était noyé. */}
            <PlanFinancementBlock dossier={dossier} dossierId={dossierId} tenantId={tenantId} onEdit={() => setShowPlanModal(true)} />

            {/* Aides complémentaires (module AG-05) — remonté dans Conformité (aux côtés
                du Plan de financement) et NON dans Finance : l'onglet Finance reste
                strictement Facture AMO (revenus TEF), cf. CLAUDE.md. Composant + backend
                /api/aides existants réutilisés tels quels (O1). Gate = capacité AMO déclarée (N°752). */}
            {amo && (
              <div data-subnav-id="aides-complementaires" className="rounded-lg overflow-hidden" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }} data-testid="bloc-aides-complementaires">
                <details open>
                  <summary className="flex items-center justify-between px-4 py-3 cursor-pointer select-none">
                    <span className="text-sm font-medium flex items-center gap-2" style={{ color: "var(--oh-text)" }}>
                      <HandCoins className="w-4 h-4" style={{ color: "var(--oh-hub-aides, var(--oh-stat-cyan-text))" }} /> Aides complémentaires
                    </span>
                    <ChevronDown className="w-4 h-4" style={{ color: "var(--oh-text-muted)" }} />
                  </summary>
                  <div className="px-4 pb-4">
                    <AidesComplementairesTab dossierId={dossierId} tenantId={tenantId} dossier={dossier} onUpdate={loadDossier} />
                  </div>
                </details>
              </div>
            )}

            <div data-subnav-id="verification" className="rounded-lg overflow-hidden" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}>
              <details open>
                <summary className="flex items-center justify-between px-4 py-3 cursor-pointer select-none">
                  <span className="text-sm font-medium flex items-center gap-2" style={{ color: "var(--oh-text)" }}>
                    <CheckCircle2 className="w-4 h-4 text-[var(--oh-info-text)]" /> Vérification Conformité
                  </span>
                  <ChevronDown className="w-4 h-4" style={{ color: "var(--oh-text-muted)" }} />
                </summary>
                <div className="px-4 pb-4">
                  {/* ⚠️ DETTE NOMMÉE : ces deux enfants LISENT `isTEF` (12 et 4
                      usages) — on leur passe désormais la CAPACITÉ. Le NOM du
                      prop reste `isTEF` pour ne pas toucher à leur intérieur la
                      veille d'une livraison ; il ⛔ ne désigne plus un slug, et
                      il est à renommer (`amo`) dans un lot dédié. */}
                  <ConformiteEditableTab dossier={dossier} tenantId={tenantId} isTEF={amo} onUpdate={loadDossier} onChecklistChange={rechargerChecklistAnah} />
                </div>
              </details>
            </div>
            </SplitViewLayout>

          </div>
        )}

        {/* ═══ ONGLET FINANCE ═══ */}
        {activeTab === "finance" && effectiveRole === "tef" && (
          <div className="space-y-4" data-testid="tab-content-finance">
            {/* Finance TEF — SPLIT VIEW (design JJ) : FinanceSection rendu TEL QUEL,
                ses 3 blocs (facture-amo / paiement / paiement-visiteur) sont DÉJÀ
                ancrés data-subnav-id, le menu gauche les montre un à un. */}
            <SplitViewLayout sections={amo ? SPLIT_SECTIONS_FINANCE : SPLIT_SECTIONS_FINANCE.filter((s) => !SECTIONS_FINANCE_AMO.has(s.id))} scanKey="finance" ariaLabel="Sections Finance">
              <FinanceSection dossier={dossier} dossierId={dossierId} tenantId={tenantId} isTEF={amo} abandoned={abandoned} onUpdate={loadDossier} />
            </SplitViewLayout>
          </div>
        )}

        {/* Refonte P1.1 — bloc « Workflow avancé » répété en bas retiré
            (le pipeline + l'action contextuelle du header couvrent la transition de statut). */}
      </div>

      {/* ═══ Modals ═══ */}
      {/* Refonte P1.1 — panneau Documents (réutilise DocumentsForm ; PAS la modale
          multicanal complète, prévue en P1.2). */}
      <Dialog open={showDocuments} onOpenChange={setShowDocuments}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Documents &amp; Photos — {dossierRef || clientDisplay}</DialogTitle></DialogHeader>
          {/* #5 — le panneau couvre désormais Documents ET Photos (dépose + consulte),
              pour conserver l'accès aux Photos après retrait du bloc embarqué d'Infos. */}
          <div className="space-y-4">
            <DocumentsForm dossierId={dossierId} tenantId={dossier?.tenant_id || tenantId} documents={documents} verrouille={dossier.verrouille || abandoned} onUpdate={loadDossier} clientNom={dossier.client_nom} dossierRef={dossier.référence} />
            <PhotosForm dossierId={dossierId} tenantId={tenantId} verrouille={dossier.verrouille || abandoned} onUpdate={loadDossier} clientNom={dossier.client_nom} dossierRef={dossier.référence} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Refonte P1.1 — panneau Commentaires (réutilise CommentairesDossier, exposé
          depuis le header : bouton/panneau, plus un bloc en bas de page). */}
      <Dialog open={showCommentaires} onOpenChange={setShowCommentaires}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Commentaires — {dossierRef || clientDisplay}</DialogTitle></DialogHeader>
          <CommentairesDossier dossierId={dossierId} tenantId={tenantId} verrouille={dossier.verrouille || abandoned} filtre={filtreCommentaires} onFiltre={setFiltreCommentaires} />
        </DialogContent>
      </Dialog>

      {/* Liaison ANAH : plus de modale — le contenu (AnahLinkTab + DonneesMPR) est
          désormais rendu INLINE dans le panneau droit du split Conformité (cohérence). */}

      {/* Conformité — MODALE Plan de financement CERFA (réutilise FinancementTab). */}
      <Dialog open={showPlanModal} onOpenChange={setShowPlanModal}>
        <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="modal-plan">
          <DialogHeader><DialogTitle>Plan de financement MPA — CERFA 13460-03 — {dossierRef || clientDisplay}</DialogTitle></DialogHeader>
          <FinancementTab dossierId={dossierId} tenantId={tenantId} dossier={dossier} onUpdate={loadDossier} />
        </DialogContent>
      </Dialog>

      {showHistorique && <HistoriqueModal historique={historique} onClose={() => setShowHistorique(false)} onVoirEchanges={() => { setShowHistorique(false); setFiltreCommentaires("echanges"); setShowCommentaires(true); }} />}
      <SendMessageModal
        open={showSendMessage}
        onClose={() => setShowSendMessage(false)}
        dossier={dossier}
        tenantId={tenantId}
        currentUser={user}
        audienceFilter={["client"]}
      />

      <TutoModal
        slug={(() => {
          const MAPPING_STEPHANIE = {
            prospect: "tuto-stephanie-01-creer-dossier",
            dossier_cree: "tuto-stephanie-01-creer-dossier",
            visite_planifiee: "tuto-stephanie-04-planifier-visite",
            visite_faite: "tuto-stephanie-04-planifier-visite",
            pret_depot: "tuto-stephanie-07-documents-anah",
            déposé: "tuto-stephanie-09-deposer-dossier",
            dossier_depose: "tuto-stephanie-09-deposer-dossier",
            instruction: "tuto-stephanie-09-deposer-dossier",
            aides_en_instruction: "tuto-stephanie-09-deposer-dossier",
            octroi: "tuto-stephanie-09-deposer-dossier",
            aides_validees: "tuto-stephanie-09-deposer-dossier",
            travaux: "tuto-stephanie-09-deposer-dossier",
            clôture: "tuto-stephanie-03-completer-dossier",
            paye: "tuto-stephanie-03-completer-dossier",
            archive: "tuto-stephanie-03-completer-dossier",
          };
          const MAPPING_INSTALLATEUR = {
            prospect: "tuto-installateur-02-voir-dossiers",
            dossier_cree: "tuto-installateur-02-voir-dossiers",
            visite_planifiee: "tuto-installateur-02-voir-dossiers",
            visite_faite: "tuto-installateur-03-ajouter-devis",
            pret_depot: "tuto-installateur-04-ajouter-facture",
            déposé: "tuto-installateur-05-photos-chantier",
            dossier_depose: "tuto-installateur-05-photos-chantier",
            instruction: "tuto-installateur-06-suivre-avancement",
            aides_en_instruction: "tuto-installateur-06-suivre-avancement",
            octroi: "tuto-installateur-04-ajouter-facture",
            aides_validees: "tuto-installateur-04-ajouter-facture",
            travaux: "tuto-installateur-05-photos-chantier",
            clôture: "tuto-installateur-06-suivre-avancement",
            paye: "tuto-installateur-06-suivre-avancement",
            archive: "tuto-installateur-06-suivre-avancement",
          };
          const mapping = (user?.role || user?.rôle) === "installateur" ? MAPPING_INSTALLATEUR : MAPPING_STEPHANIE;
          const step = pipelineStep || dossier?.statut;
          return mapping[step] || ((user?.role || user?.rôle) === "installateur" ? "tuto-installateur-02-voir-dossiers" : "tuto-stephanie-01-creer-dossier");
        })()}
        isOpen={showTuto}
        onClose={() => setShowTuto(false)}
        tenantId={tenantId}
      />
    </div>
  );
}

// 🔴 LES VISITES DU DOSSIER — la relation existait, l'écran la rendait invisible.
//
// ⚠️ MESURÉ le 18/09 : la visite porte `dossier_id` et `dossier_reference`, le
// devis porte `visite_id`, la visite porte `devis_id`. La chaîne est COMPLÈTE
// et BIDIRECTIONNELLE en base — mais AUCUN écran ⛔ ne permettait d'aller du
// dossier à sa visite, donc ⛔ ni à son RAPPORT. « Dossier → Visite → Rapport »
// était impossible par l'interface, alors que la donnée était là.
// 🔑 CE COMPOSANT LIT ET AFFICHE. ⛔ Il ⛔ ne CRÉE rien, ⛔ ne RATTACHE rien,
// ⛔ ne DUPLIQUE aucun écran de rapport : le rapport est servi par la route qui
// existe déjà.
function VisitesDuDossier({ dossierId, tenantId }) {
  const [visites, setVisites] = useState(null);
  const [erreur, setErreur] = useState(null);
  // 🔴 N°648 — le rapport se DEMANDE avec l'en-tête, il ⛔ ne se NAVIGUE pas.
  const [erreurRapport, setErreurRapport] = useState(null);
  // 🔑 N°673 — SOURCE UNIQUE `openAuthedPdf` : mon `ouvrirProtege` (N°648)
  // DOUBLAIT cette fonction, qui existait AVANT lui dans `dossier/helpers`.
  // ⚠️ Je ⛔ n'avais PAS grepé (O1) — deux implémentations identiques
  // divergent toujours. Le doublon est SUPPRIMÉ, ⛔ pas laissé en place.
  const ouvrirRapport = (id) => {
    setErreurRapport(null);
    return openAuthedPdf({
      url: `${API}/api/visite/saisies/${id}/rapport`,
      headers: getAuthHeaders(tenantId),
      onError: (msg, statut) => setErreurRapport(
        (statut === 401 || statut === 403)
          ? "Session expirée : reconnectez-vous pour ouvrir le rapport."
          : msg),
    });
  };

  // 🔴 N°829 — le chargement devient une fonction : on RECHARGE après une suppression.
  const [recharge, setRecharge] = useState(0);
  // 🔴 N°829 — SUPPRIMER une visite non terminée, comme un devis brouillon
  // (N°690). La garde est côté SERVEUR (terminée ou avec devis ⇒ refus motivé) :
  // ce bouton n'est que la porte. On DEMANDE avant : ça ne se rattrape pas.
  const supprimerVisite = async (v) => {
    if (!window.confirm(
      `Supprimer la visite du ${formatDateFR(v.created_at) || ""} ?\n\n`
      + "Ses liens envoyés cesseront de fonctionner. Les photos restent dans le dossier. "
      + "Cette action ne s'annule pas.")) return;
    try {
      const res = await fetch(`${API}/api/visite/saisies/${encodeURIComponent(v.id)}`,
        { method: "DELETE", headers: getAuthHeaders(tenantId), credentials: "include" });
      const d = await res.json().catch(() => ({}));
      if (res.ok) { toast.success("Visite supprimée"); setRecharge((n) => n + 1); }
      else toast.error(typeof d.detail === "string" ? d.detail
        : `Suppression refusée (erreur ${res.status})`);
    } catch { toast.error("Réseau indisponible : la visite n'a pas été supprimée."); }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(
          `${API}/api/visite/saisies?dossier_id=${encodeURIComponent(dossierId)}`,
          { headers: getAuthHeaders(tenantId), credentials: "include" });
        if (!res.ok) throw new Error(`Erreur ${res.status}`);
        const d = await res.json();
        // 🔴 N°696-B — `visites` DIT CE QUE C'EST ; `en_cours` reste le repli
        // tant que tous les serveurs ⛔ ne l'ont PAS. ⛔ Aucun filtre ICI : la
        // sélection appartient au serveur, source unique.
        setVisites(d.visites || d.en_cours || []);
      } catch (e) { setErreur(e.message); }
    })();
  }, [dossierId, tenantId, recharge]);

  if (erreur) {
    return (
      <div className="p-4 rounded-lg" data-testid="visites-erreur"
           style={{ background: "var(--oh-stat-red-bg)",
                    color: "var(--oh-stat-red-text)" }}>{erreur}</div>
    );
  }
  if (visites === null) {
    return <div className="p-4 text-[var(--oh-text-muted)]">Chargement…</div>;
  }
  if (!visites.length) {
    // ⛔ Un écran vide sans phrase se lit comme une panne. On DIT l'état.
    return (
      <div className="p-6 text-center" data-testid="visites-aucune">
        <p style={{ color: "var(--oh-text)" }}>Aucune visite sur ce dossier.</p>
        <p className="text-sm mt-1" style={{ color: "var(--oh-text-secondary)" }}>
          Les visites se créent depuis l'application terrain.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="visites-liste">
      {/* ⛔ Un rapport qui ne s'ouvre pas DOIT le dire — un onglet blanc se lit
          comme une panne de l'application. */}
      {erreurRapport && (
        <div className="p-3 rounded-lg text-sm" data-testid="visite-rapport-erreur"
             style={{ background: "var(--oh-stat-red-bg)",
                      color: "var(--oh-stat-red-text)" }}>{erreurRapport}</div>
      )}
      {visites.map((v) => (
        <div key={v.id} data-testid="visite-ligne"
             className="p-4 rounded-lg border"
             style={{ background: "var(--oh-bg-elevated)",
                      borderColor: "var(--oh-border)" }}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p style={{ color: "var(--oh-text)", fontWeight: 500 }}>
                {v.profil_code}
              </p>
              {/* 🔴 N°696-B — LA DATE DISTINGUE LES PASSAGES. Un chantier
                  revisité trois fois porte trois lignes : sans la date de
                  CLÔTURE, l'écran disait « Clôturée » ⛔ sans dire QUAND. */}
              <p className="text-xs" style={{ color: "var(--oh-text-secondary)" }}>
                Visite du {formatDateFR(v.created_at) || (v.created_at || "").slice(0, 10)}
                {v.cloturee_le ? ` · clôturée le ${formatDateFR(v.cloturee_le)}` : ""}
                {v.abandonnee_le ? ` · abandonnée le ${formatDateFR(v.abandonnee_le)}` : ""}
                {" · "}{v.nb_reponses} réponse(s)
                · {v.nb_observations} observation(s)
                {v.nb_bloquantes ? ` · ${v.nb_bloquantes} réserve(s) bloquante(s)` : ""}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* 🔴 N°584 — TROIS états, ⛔ plus deux : « tout ce qui n'est pas
                  clôturé est en cours » aurait présenté une visite ABANDONNÉE
                  comme ACTIVE. */}
              <span className="text-xs px-2 py-1 rounded"
                    data-testid="visite-statut"
                    style={v.statut === "terminee"
                      ? { background: "var(--oh-stat-green-bg)", color: "var(--oh-stat-green-text)" }
                      : v.statut === "abandonnee"
                        ? { background: "var(--oh-bg-surface)", color: "var(--oh-text-secondary)",
                            border: "0.5px solid var(--oh-border)" }
                        : { background: "var(--oh-stat-amber-bg)", color: "var(--oh-stat-amber-text)" }}>
                {v.statut === "terminee" ? "Clôturée"
                  : v.statut === "abandonnee"
                    ? `Abandonnée${v.abandonnee_le ? ` le ${(v.abandonnee_le || "").slice(0, 10)}` : ""}`
                    : "En cours"}
              </span>
              {/* 🔑 LE RAPPORT — la route existe déjà, ⛔ on n'en crée pas une.
                  🔴 N°648 — ⛔ PLUS un `<a href>` : une navigation ⛔ ne porte
                  PAS le jeton `Bearer` de `localStorage` ⇒ 401. On le demande
                  par `fetch` AVEC l'en-tête, et on ouvre le blob.
                  ⚠️ PORT INTÉGRATION : le libellé à TROIS états vient de `main`
                  (il est POSTÉRIEUR à la coupe de cette branche) — le mécanisme
                  du lot + le libellé de main, ⛔ aucun des deux n'est annulé. */}
              <button type="button" onClick={() => ouvrirRapport(v.id)}
                      data-testid="visite-rapport"
                      className="text-sm underline"
                      style={{ color: "var(--oh-accent-text)", background: "none",
                               border: "none", padding: 0, cursor: "pointer" }}>
                {v.statut === "terminee" ? "Rapport"
                  : v.statut === "abandonnee" ? "Rapport (visite abandonnée)"
                  : "Rapport provisoire"}
              </button>
              {/* 🔑 LA RELATION AVEC LE DEVIS, CONSTATÉE — ⛔ pas fabriquée. */}
              {v.devis_id && (
                <span className="text-xs" data-testid="visite-devis"
                      style={{ color: "var(--oh-text-secondary)" }}>
                  · a produit un devis
                </span>
              )}
              {/* 🔴 N°829 — seulement une visite NON terminée et SANS devis :
                  ce que le serveur accepte, ⛔ rien de plus. */}
              {["brouillon", "abandonnee"].includes(v.statut) && !v.devis_id && (
                <button type="button" onClick={() => supprimerVisite(v)}
                        data-testid="visite-supprimer"
                        className="text-sm underline"
                        style={{ color: "var(--oh-stat-red-text)", background: "none",
                                 border: "none", padding: 0, cursor: "pointer" }}>
                  Supprimer
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
